import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { env } from '@/shared/config/env'
import { ApiError } from '@/shared/api/http'

import { authApi } from '../api/auth.api'
import type {
  AdminSessionResponse,
  AdminSessionUser,
  AuthUser,
  LoginCredentials,
} from '../types'

const LEGACY_CREDENTIAL_KEYS = [
  'convbased-access-token',
  'convbased-token-expires-at',
  'convbased-user',
] as const
const CLIENT_ID_KEY = 'convbased-admin-client-id'
export const ADMIN_SESSION_EVENT_KEY = 'convbased-admin-session-event'
const CLIENT_ID_PATTERN = /^[A-Za-z0-9._:-]{8,128}$/

export type AdminSessionEvent = 'authenticated' | 'signed-out'

function clearLegacyCredentialStorage() {
  for (const key of LEGACY_CREDENTIAL_KEYS) window.localStorage.removeItem(key)
}

function adminClientId(): string {
  const stored = window.localStorage.getItem(CLIENT_ID_KEY)
  if (stored && CLIENT_ID_PATTERN.test(stored)) return stored
  const generated = `admin-web:${crypto.randomUUID()}`
  window.localStorage.setItem(CLIENT_ID_KEY, generated)
  return generated
}

function publishSessionEvent(type: AdminSessionEvent) {
  const event = JSON.stringify({ id: crypto.randomUUID(), type })
  window.localStorage.setItem(ADMIN_SESSION_EVENT_KEY, event)
  window.localStorage.removeItem(ADMIN_SESSION_EVENT_KEY)
}

const demoUser: AuthUser = {
  id: 'usr_admin',
  name: 'Iker Chen',
  email: 'admin@convbased.com',
  role: 'owner',
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const accessTokenExpiresAt = ref(0)
  const user = ref<AuthUser | null>(null)
  const initialized = ref(false)
  let initialization: Promise<void> | null = null
  let refreshing: Promise<string | null> | null = null

  const isAuthenticated = computed(
    () =>
      Boolean(accessToken.value && user.value) &&
      (accessTokenExpiresAt.value === 0 || Date.now() < accessTokenExpiresAt.value),
  )

  function acceptSession(session: AdminSessionResponse): string {
    if (session.client_id !== adminClientId()) throw new Error('管理会话客户端不匹配')
    const expiresAt = Date.parse(session.expires_at)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      throw new Error('管理会话已过期')
    }
    accessToken.value = session.access_token
    accessTokenExpiresAt.value = expiresAt
    return session.access_token
  }

  function mapAdminUser(admin: AdminSessionUser): AuthUser {
    return {
      id: admin.id,
      name: admin.nickname?.trim() || admin.username?.trim() || admin.email,
      email: admin.email,
      role: 'owner',
    }
  }

  function clearSession() {
    accessToken.value = null
    accessTokenExpiresAt.value = 0
    user.value = null
    clearLegacyCredentialStorage()
  }

  async function refreshAccessToken(): Promise<string | null> {
    if (env.authMode === 'demo') return accessToken.value
    if (refreshing) return refreshing

    refreshing = (async () => {
      let session: AdminSessionResponse
      try {
        session = await authApi.refresh()
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 409) throw error
        await new Promise((resolve) => window.setTimeout(resolve, 100))
        session = await authApi.refresh()
      }
      return acceptSession(session)
    })().finally(() => {
      refreshing = null
    })

    return refreshing
  }

  async function restoreSession() {
    const token = await refreshAccessToken()
    if (!token) return
    user.value = mapAdminUser(await authApi.me())
  }

  async function initialize() {
    if (initialized.value) return
    if (initialization) return initialization

    initialization = (async () => {
      clearLegacyCredentialStorage()

      if (env.authMode === 'demo') {
        clearSession()
        accessToken.value = 'demo-session'
        user.value = demoUser
        return
      }

      try {
        await restoreSession()
      } catch {
        clearSession()
      }
    })().finally(() => {
      initialized.value = true
      initialization = null
    })

    return initialization
  }

  async function login(credentials: LoginCredentials) {
    if (env.authMode === 'demo') {
      await new Promise((resolve) => window.setTimeout(resolve, 350))
      accessToken.value = 'demo-session'
      user.value = { ...demoUser, email: credentials.email }
      return
    }

    clearSession()

    try {
      const session = await authApi.login({ ...credentials, client_id: adminClientId() })
      acceptSession(session)
      user.value = mapAdminUser(await authApi.me())
      publishSessionEvent('authenticated')
    } catch (error) {
      await authApi.logout().catch(() => undefined)
      clearSession()
      throw error
    }
  }

  async function logout() {
    try {
      if (env.authMode === 'required') await authApi.logout()
    } finally {
      clearSession()
      publishSessionEvent('signed-out')
    }
  }

  async function syncSessionEvent(type: AdminSessionEvent) {
    if (type === 'signed-out') {
      clearSession()
      return
    }
    if (env.authMode !== 'required') return
    try {
      await restoreSession()
    } catch {
      clearSession()
    }
  }

  return {
    accessToken,
    accessTokenExpiresAt,
    user,
    initialized,
    isAuthenticated,
    initialize,
    refreshAccessToken,
    syncSessionEvent,
    login,
    logout,
    clearSession,
  }
})
